import { makeStyles } from '@material-ui/core/styles'
import Tooltip from '@material-ui/core/Tooltip'
import InfoIcon from '@material-ui/icons/Info'
import Alert from '@material-ui/lab/Alert'
import get from 'lodash/get'
import { DateTime } from 'luxon'
import { useCallback } from 'react'
import {
  ArrayInput,
  BooleanInput,
  Create,
  FormDataConsumer,
  FormTab,
  maxLength,
  minLength,
  number,
  NumberInput,
  required,
  SaveButton,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextInput,
  Toolbar,
  useCreate,
  useNotify,
  useRedirect,
} from 'react-admin'
import { useFormState } from 'react-final-form'
import { SHIPPER_SOURCE_NAME_CHOICES, SOURCE_NAME_CHOICES } from 'shared/helpers/constants'
import { information, primaryBlack } from 'shared/theme/colors'

import { StopDateTimeInputWindow, StopSequenceNumberWrapper } from 'components/Fields'
import {
  CustomLoadDefaultSource,
  DESTINATION,
  LoadingType,
  LoadingTypeChoices,
  ORIGIN,
  STOP_TYPE_CHOICES,
  StopTypeDetail,
  StopTypeDetailChoices,
  SUPPORTED_EQUIPMENT_CHOICES,
} from 'helpers/constants'
import {
  getLoadStop,
  localizeDateTimeAndStripOffset,
  sanitizeNonNullableFieldsOnLoad,
  stripOffsetsFromStopDateTimes,
} from 'helpers/utils'
import { useHasCustomLoadsInstantBook } from 'hooks/useHasFeatureFlag'
import { getCountryFromCode } from '../../helpers/countries'
import { getStateChoices } from '../../helpers/states'
import { validateInteger, validateLoadStops, validateTwoDecimals } from '../../helpers/validators'
import { BrokerStatus } from '../Brokers/BrokerStatusTransitions'
import { BrokerReferenceInput, BrokerReferenceInputClassName } from '../Common/Brokers'

const useStyles = makeStyles({
  virtualSource: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  virtualSourceTooltip: {
    padding: '24px 20px 28px',

    maxWidth: 'none !important',
    width: 340,

    background: information,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
    borderRadius: 6,
    color: primaryBlack,
    fontSize: 12,
  },
  virtualSourceIcon: {
    marginLeft: '5px',
    marginBottom: '15px',
  },
})

const getCustomLoadInitialValues = (customLoad: any) => {
  if (customLoad) {
    if (customLoad.broker) {
      customLoad.is_shipper = customLoad.broker.is_shipper
      customLoad.broker_id = customLoad.broker.id
    }

    customLoad.stops = customLoad.load_stops.map((stop: any) => ({
      ...stop,
      date_start: localizeDateTimeAndStripOffset(stop.date_start, stop.location_timezone),
      date_end: localizeDateTimeAndStripOffset(stop.date_end, stop.location_timezone),
      notes: '',
      id: null,
      location_timezone: undefined,
    }))
    customLoad.source = customLoad.external_source

    if (customLoad.contact) {
      customLoad.contact_name = customLoad.contact.name
      customLoad.contact_phone = customLoad.contact.phone
    }

    return {
      ...customLoad,
      id: undefined,
    }
  }

  const defaultDate = DateTime.local().set({ hour: 0, minute: 0 })

  return {
    origin_location: '',
    dest_location: '',
    internal_notes: '',
    origin_pickup_date: '',
    dest_delivery_date: '',
    external_source_human_id: '',
    trip_stops: 2,
    stops: [
      {
        sequence_number: 1,
        type: ORIGIN,
        type_detail: StopTypeDetail.Pickup,
        loading_type: LoadingType.Live,
        date_start: defaultDate,
        date_end: defaultDate,
      },
      {
        sequence_number: 2,
        type: DESTINATION,
        type_detail: StopTypeDetail.Delivery,
        loading_type: LoadingType.Live,
        date_start: defaultDate,
        date_end: defaultDate,
      },
    ],
  }
}

const validateCustomLoadCreation = (values: any) => {
  var errors = validateLoadStops(values)
  if (errors & errors.load_stops) {
    errors.load_stops = [errors.load_stops]
  }
  return errors
}

const validateTripRate = [number(), validateTwoDecimals]

const SaveButtonAltered = (props: any) => {
  const notify = useNotify()
  const redirectTo = useRedirect()
  const [create] = useCreate('custom-loads')
  const { basePath } = props
  const formState = useFormState()

  const submitForm = useCallback(
    (values, redirect) => {
      if (formState.hasValidationErrors) {
        return
      }
      values = stripOffsetsFromStopDateTimes(values, false)
      values = sanitizeNonNullableFieldsOnLoad(values, false)
      let originStop = getLoadStop(values, ORIGIN)
      let destStop = getLoadStop(values, DESTINATION)
      let numStops = values.stops.length
      let equipment = typeof values.equipment === 'string' ? [values.equipment] : values.equipment
      let sortedStops = values.stops.sort((a: any, b: any) => a.sequence_number - b.sequence_number)
      if (!values.source) {
        values.source = CustomLoadDefaultSource
      }
      values.external_source = values.source
      // TODO: leave as origin_location or swap to city/state?
      values.origin_location = `${originStop.location_city}, ${originStop.location_state}`
      values.dest_location = `${destStop.location_city}, ${destStop.location_state}`
      values.origin_pickup_date = originStop.date_start
      values.dest_delivery_date = destStop.date_start
      values.external_source_human_id = values.external_source_id
      values.equipment = equipment
      values.trip_stops = numStops
      values.load_stops = sortedStops
      create(
        { payload: { data: { ...values } } },
        {
          onSuccess: ({ data: newRecord }: any) => {
            notify('CustomLoad saved', 'info', { smart_count: 1 })
            redirectTo(redirect, basePath, newRecord.id, newRecord)
          },
          onFailure: (error: any) => {
            notify(`${JSON.stringify(error)}`, { type: 'error' })
          },
        }
      )
    },
    [create, notify, basePath, redirectTo, formState]
  )

  const loadStopErrors = formState.errors?.load_stops
  if (loadStopErrors) {
    return <Alert severity="error">There is an error with the form: {loadStopErrors}</Alert>
  }

  return <SaveButton {...props} onSave={submitForm} />
}

const CustomLoadCreateToolbar = (props: any) => (
  <Toolbar {...props}>
    <SaveButtonAltered label="Save" redirect="show" submitOnEnter={false} variant="text" />
  </Toolbar>
)

const BrokerShipperReferenceInput = (isShipper: boolean, rest: any) => {
  const filter = isShipper
    ? { status__not: BrokerStatus.Invalid.id, is_shipper: true }
    : { status__not: BrokerStatus.Invalid.id }

  return (
    <BrokerReferenceInput
      source="broker_id"
      label="Broker"
      reference="brokers"
      filter={filter}
      filterToQuery={(searchText: any) => ({ search: searchText })}
      containerClassName={BrokerReferenceInputClassName.load}
      validate={required()}
      {...rest}
    />
  )
}

const VirtualSourceInput = (isShipper: Boolean, props: any) => {
  const classes = useStyles()

  const choices = isShipper ? SHIPPER_SOURCE_NAME_CHOICES : SOURCE_NAME_CHOICES

  return (
    <div className={classes.virtualSource}>
      <SelectInput source="virtual_source" choices={choices} {...props} />
      <Tooltip
        placement="right"
        title="If a virtual source is provided, the load will be returned as part of the search results for that source (and not CloudTrucks)."
        classes={{ tooltip: classes.virtualSourceTooltip }}
      >
        <InfoIcon color="primary" fontSize="small" className={classes.virtualSourceIcon} />
      </Tooltip>
    </div>
  )
}

const CustomLoadCreate = (props: any) => {
  const customLoad = get(props, 'location.state.customLoad')
  const country = getCountryFromCode('US')
  const stateChoices = getStateChoices(country?.states(true))
  const hasCustomLoadsInstantBook = useHasCustomLoadsInstantBook()

  return (
    <Create {...props}>
      <TabbedForm
        toolbar={<CustomLoadCreateToolbar />}
        initialValues={getCustomLoadInitialValues(customLoad)}
        validate={validateCustomLoadCreation}
      >
        <FormTab label="Contact Information">
          <BooleanInput source="is_shipper" label="Shipper Load" />
          <FormDataConsumer>
            {({ formData, ...rest }) =>
              !formData.is_shipper && <SelectInput source="source" choices={SOURCE_NAME_CHOICES} {...rest} />
            }
          </FormDataConsumer>
          <FormDataConsumer>
            {({ formData, ...rest }) => VirtualSourceInput(formData.is_shipper, rest)}
          </FormDataConsumer>
          <TextInput source="external_source_id" label="Source ID" validate={required()} />
          <FormDataConsumer>
            {({ formData, ...rest }) => BrokerShipperReferenceInput(formData.is_shipper, rest)}
          </FormDataConsumer>
          <TextInput source="company_name" label="Shipper name" />
          <TextInput source="contact_name" />
          <TextInput source="contact_email" />
          <TextInput source="contact_phone" />
          <TextInput source="internal_notes" multiline fullWidth label="Internal Notes" />
        </FormTab>
        <FormTab label="Stops">
          <ArrayInput source="stops">
            <SimpleFormIterator>
              <StopSequenceNumberWrapper label="Sequence number" validate={required()} />
              <SelectInput
                source="type"
                choices={STOP_TYPE_CHOICES}
                label="Stop type"
                validate={required()}
              />
              <SelectInput
                source="type_detail"
                label="Type detail"
                choices={StopTypeDetailChoices}
                validate={required()}
              />
              <SelectInput source="loading_type" label="Loading type" choices={LoadingTypeChoices} />
              <StopDateTimeInputWindow />
              <TextInput source="location_name" label="Facility name" />
              <TextInput source="location_address1" label="Location address 1" />
              <TextInput source="location_address2" label="Location address 2" />
              <TextInput source="location_city" label="City" validate={required()} />
              <SelectInput
                source="location_state"
                label="State"
                choices={stateChoices}
                validate={[required(), maxLength(2), minLength(2)]}
              />
              <TextInput source="location_zip" label="Zip" />
              <TextInput source="notes" multiline fullWidth label="Notes" />
              <TextInput source="reference_code" label="Reference code" validate={maxLength(250)} />
            </SimpleFormIterator>
          </ArrayInput>
        </FormTab>
        <FormTab label="Trip Details">
          <SelectInput source="equipment" choices={SUPPORTED_EQUIPMENT_CHOICES} validate={required()} />
          <BooleanInput source="standard_book" />
          {hasCustomLoadsInstantBook && <BooleanInput source="instant_book" />}
          <NumberInput source="trip_rate" validate={validateTripRate} />
          <NumberInput source="trip_distance_mi" validate={[required(), validateInteger]} />
          <BooleanInput source="truck_full_load" label="Full truck load" />
          <NumberInput source="truck_length_ft" validate={validateInteger} />
          <NumberInput source="truck_weight_lb" validate={validateInteger} />
          <BooleanInput source="show_in_search" label="Show on Loadboard" />
          <BooleanInput source="ghost_load" label="Ghost Load" />
        </FormTab>
      </TabbedForm>
    </Create>
  )
}

export default CustomLoadCreate
